import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type PointerEvent, type WheelEvent } from 'react';
import { AnimationController, type AnimationSnapshot, type Guide } from '../../animation/animationController';
import { displayScene } from '../../animation/timeline';
import * as S from '../../domain/sceneOps';
import type { Animation, Scene, SceneObject } from '../../domain/schemas';
import { debugCounters } from '../../lib/debug';
import type { Observable } from '../../lib/store';
import { useObservable, useObservableSelector } from '../../lib/useObservable';
import * as G from '../../scene/geometry';
import { useVisualHistory } from '../../state/historyContext';
import { useProject } from '../../state/projectContext';
import { useServices } from '../../state/services';
import { Spinner } from '../ui';
import type { TransformEditFn } from './useAnimationEditing';
import type { EditFn } from './useSceneEdit';

const NO_GUIDES: readonly Guide[] = [];

type Drag =
  | { mode: 'move'; pointerId: number; start: G.Point; ids: string[]; origin: Map<string, SceneObject>; moved: boolean }
  | { mode: 'resize'; pointerId: number; handle: Exclude<G.Handle, 'rotate'>; object: SceneObject }
  | { mode: 'rotate'; pointerId: number; object: SceneObject }
  | { mode: 'pan'; pointerId: number; startScreen: G.Point; camera: G.Camera }
  | { mode: 'marquee'; pointerId: number; start: G.Point; current: G.Point; additive: boolean };

export interface CanvasApi {
  camera: G.Camera;
  setCamera: (c: G.Camera) => void;
  fit: () => void;
  /** Scene point at the centre of the view (where new objects are placed). */
  viewCentre: () => G.Point;
}

/**
 * The scene canvas: a PixiJS view plus a transparent DOM layer that handles pointer,
 * wheel and keyboard input. Pointer positions are converted to scene coordinates through
 * the camera, so selection, moving, resizing and rotating stay exact at any zoom. Drags
 * only preview on the renderer and commit one project change (one undo step) on release.
 *
 * With an animation selected, the canvas shows (and hit-tests) objects as displayed at the
 * playhead. Transform edits go through `onTransform`, which keys them (Auto-Key) or edits
 * base properties.
 */
export function SceneCanvas({
  scene,
  state,
  onEdit,
  onTransform,
  animation,
  guides = NO_GUIDES,
  apiRef,
  controllerRef,
}: {
  scene: Scene;
  state: Observable<AnimationSnapshot>;
  onEdit: EditFn;
  onTransform: TransformEditFn;
  animation: Animation | null;
  /** Motion Composer preview paths and trajectories (drawn over the scene, never saved). */
  guides?: readonly Guide[];
  apiRef: React.MutableRefObject<CanvasApi | null>;
  controllerRef: React.MutableRefObject<AnimationController | null>;
}) {
  const { project } = useProject();
  const { images, selection, animation: bus } = useServices();
  const history = useVisualHistory();
  const selected = useObservable(selection);
  const status = useObservableSelector(state, (v) => v.status);
  const stageRef = useRef<HTMLDivElement>(null);
  const [camera, setCameraState] = useState<G.Camera>({ zoom: 0.5, panX: 0, panY: 0 });
  const cameraRef = useRef(camera);
  const autoFit = useRef(true);
  const drag = useRef<Drag | null>(null);
  const [marquee, setMarquee] = useState<{ x: number; y: number; w: number; h: number } | null>(null);
  const [announce, setAnnounce] = useState('');
  const sceneRef = useRef(scene);
  const charactersRef = useRef(project.characters);
  const animationRef = useRef(animation);

  const setCamera = (c: G.Camera) => {
    cameraRef.current = c;
    setCameraState(c);
    controllerRef.current?.setCamera(c);
  };
  const fit = () => {
    const view = controllerRef.current?.viewSize ?? { width: stageRef.current?.clientWidth ?? 0, height: stageRef.current?.clientHeight ?? 0 };
    if (view.width < 2) return;
    autoFit.current = true;
    setCamera(G.fitCamera(sceneRef.current.width, sceneRef.current.height, view.width, view.height));
  };
  const userCamera = (c: G.Camera) => {
    autoFit.current = false;
    setCamera(c);
  };
  const fitRef = useRef(fit);
  // Keep refs used by event handlers and the renderer callbacks current after each render.
  useLayoutEffect(() => {
    sceneRef.current = scene;
    charactersRef.current = project.characters;
    animationRef.current = animation;
    fitRef.current = fit;
    apiRef.current = {
      camera,
      setCamera: userCamera,
      fit,
      viewCentre: () => {
        const view = controllerRef.current?.viewSize ?? { width: 0, height: 0 };
        return G.screenToScene(cameraRef.current, { x: view.width / 2, y: view.height / 2 });
      },
    };
  });

  // One renderer per mount (StrictMode-safe); destroyed on unmount.
  useEffect(() => {
    const stage = stageRef.current;
    if (!stage) return;
    const controller = new AnimationController(undefined, state, {
      loadImage: (id) => images.load(id),
      onResize: () => {
        if (autoFit.current) fitRef.current();
      },
      clock: bus.clock,
      effectsQuality: bus.draftEffects.get() ? 'draft' : 'full',
    });
    controllerRef.current = controller;
    debugCounters.sceneStats = () => controller.stats();
    debugCounters.animationState = () => ({ frame: controller.stats().frame, playing: bus.clock.get().playing, values: Object.fromEntries(controller.evaluatedState()) });
    // Phase 5B.0 test hook: compiled out of the production build (see vite.config.ts).
    if (__SONICFORGE_TEST_HOOKS__) {
      debugCounters.animationSeek = (frame) => {
        const t0 = performance.now();
        bus.clock.seek(frame);
        return performance.now() - t0;
      };
    }
    void controller.mount(stage, sceneRef.current, charactersRef.current).then(() => {
      if (controllerRef.current === controller) {
        controller.setCamera(cameraRef.current);
        controller.setAnimation(animationRef.current);
        fitRef.current();
      }
    });
    return () => {
      controller.destroy();
      if (controllerRef.current === controller) controllerRef.current = null;
      debugCounters.sceneStats = () => null;
      debugCounters.animationState = () => null;
      if (__SONICFORGE_TEST_HOOKS__) debugCounters.animationSeek = undefined;
    };
  }, [state, images, controllerRef, bus]);

  const draftEffects = useObservable(bus.draftEffects);
  useEffect(() => {
    controllerRef.current?.setEffectsQuality(draftEffects ? 'draft' : 'full');
  }, [draftEffects, controllerRef]);

  useEffect(() => {
    controllerRef.current?.setScene(scene);
  }, [scene, controllerRef]);
  useEffect(() => {
    controllerRef.current?.setCharacters(project.characters);
  }, [project.characters, controllerRef]);
  useEffect(() => {
    controllerRef.current?.setAnimation(animation);
  }, [animation, controllerRef, status]);
  useEffect(() => {
    controllerRef.current?.setSelection(selected.objectIds);
  }, [selected.objectIds, controllerRef, status]);
  useEffect(() => {
    controllerRef.current?.setGuides(guides);
  }, [guides, controllerRef, status]);
  // Switching scenes refits the view.
  useEffect(() => {
    fitRef.current();
  }, [scene.id, scene.width, scene.height]);

  const ready = status === 'paused' || status === 'running';
  /** The scene as displayed at the playhead (what the user sees and clicks). */
  const shownScene = () => displayScene(scene, controllerRef.current?.evaluatedState() ?? null);
  const selectedObjects = scene.objects.filter((o) => selected.objectIds.includes(o.id));

  const toScene = (e: { clientX: number; clientY: number }) => {
    const rect = stageRef.current!.getBoundingClientRect();
    return G.screenToScene(cameraRef.current, { x: e.clientX - rect.left, y: e.clientY - rect.top });
  };
  const toScreen = (e: { clientX: number; clientY: number }) => {
    const rect = stageRef.current!.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const select = (ids: string[]) => {
    selection.selectObjects(ids);
    const names = scene.objects.filter((o) => ids.includes(o.id)).map((o) => o.name);
    setAnnounce(names.length === 0 ? 'Nothing selected' : names.length === 1 ? `Selected ${names[0]}` : `Selected ${names.length} objects`);
  };

  const onPointerDown = (e: PointerEvent<HTMLDivElement>) => {
    if (!ready) return;
    e.currentTarget.focus();
    const p = toScene(e);
    const pan = e.button === 1 || e.button === 2 || e.altKey;
    const shown = shownScene();
    const shownSelected = shown.objects.filter((o) => selected.objectIds.includes(o.id));
    if (!pan && shownSelected.length === 1 && !S.isLocked(scene, shownSelected[0]!)) {
      const o = shownSelected[0]!;
      const handle = G.handleAt(o, p, cameraRef.current.zoom);
      if (handle) {
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = handle === 'rotate' ? { mode: 'rotate', pointerId: e.pointerId, object: o } : { mode: 'resize', pointerId: e.pointerId, handle, object: o };
        return;
      }
    }
    const hit = pan ? null : G.hitTest(shown, p);
    e.currentTarget.setPointerCapture(e.pointerId);
    if (hit) {
      let ids = selected.objectIds;
      if (e.shiftKey) ids = ids.includes(hit.id) ? ids.filter((id) => id !== hit.id) : S.expandToGroups(scene, [...ids, hit.id]);
      else if (!ids.includes(hit.id)) ids = S.expandToGroups(scene, [hit.id]);
      select(ids);
      const movable = shown.objects.filter((o) => ids.includes(o.id) && !S.isLocked(scene, o));
      drag.current = { mode: 'move', pointerId: e.pointerId, start: p, ids: movable.map((o) => o.id), origin: new Map(movable.map((o) => [o.id, o])), moved: false };
      return;
    }
    // Empty space: mouse drags a selection rectangle; touch, middle/right button or Alt pans.
    if (pan || e.pointerType === 'touch') {
      drag.current = { mode: 'pan', pointerId: e.pointerId, startScreen: toScreen(e), camera: cameraRef.current };
      if (!e.shiftKey && e.pointerType === 'touch') select([]);
      return;
    }
    if (!e.shiftKey) select([]);
    drag.current = { mode: 'marquee', pointerId: e.pointerId, start: p, current: p, additive: e.shiftKey };
  };

  const onPointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const p = toScene(e);
    const controller = controllerRef.current;
    switch (d.mode) {
      case 'move': {
        const dx = p.x - d.start.x;
        const dy = p.y - d.start.y;
        if (!d.moved && Math.hypot(dx, dy) * cameraRef.current.zoom < 3) return;
        d.moved = true;
        controller?.setPreview(new Map([...d.origin].map(([id, o]) => [id, { x: o.x + dx, y: o.y + dy }])));
        break;
      }
      case 'resize': {
        const keep = e.shiftKey || d.object.type === 'circle';
        controller?.setPreview(new Map([[d.object.id, G.resizeBox(d.object, d.handle, p, keep)]]));
        break;
      }
      case 'rotate':
        controller?.setPreview(new Map([[d.object.id, { rotation: G.rotationTowards(d.object, p, e.shiftKey) }]]));
        break;
      case 'pan': {
        const s = toScreen(e);
        userCamera(G.panBy(d.camera, s.x - d.startScreen.x, s.y - d.startScreen.y));
        break;
      }
      case 'marquee': {
        d.current = p;
        const a = G.sceneToScreen(cameraRef.current, d.start);
        const b = G.sceneToScreen(cameraRef.current, p);
        setMarquee({ x: Math.min(a.x, b.x), y: Math.min(a.y, b.y), w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) });
        break;
      }
    }
  };

  const finish = (e: PointerEvent<HTMLDivElement>, cancelled = false) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    const controller = controllerRef.current;
    const p = toScene(e);
    if (cancelled) {
      controller?.setPreview(null);
      setMarquee(null);
      return;
    }
    // The drag's preview always ends here. (An Auto-Key drag changes only the animation,
    // not the scene, so nothing else would clear it.)
    controller?.setPreview(null);
    switch (d.mode) {
      case 'move':
        if (d.moved && d.ids.length) {
          const dx = Math.round((p.x - d.start.x) * 100) / 100;
          const dy = Math.round((p.y - d.start.y) * 100) / 100;
          onTransform('Move', d.ids, (o) => ({ x: o.x + dx, y: o.y + dy }));
        }
        break;
      case 'resize': {
        const box = G.resizeBox(d.object, d.handle, p, e.shiftKey || d.object.type === 'circle');
        onTransform('Resize', [d.object.id], () => box);
        break;
      }
      case 'rotate': {
        const rotation = G.rotationTowards(d.object, p, e.shiftKey);
        onTransform('Rotate', [d.object.id], () => ({ rotation }));
        break;
      }
      case 'marquee': {
        setMarquee(null);
        const r = { x: Math.min(d.start.x, p.x), y: Math.min(d.start.y, p.y), width: Math.abs(p.x - d.start.x), height: Math.abs(p.y - d.start.y) };
        if (r.width * cameraRef.current.zoom < 4 && r.height * cameraRef.current.zoom < 4) break;
        const ids = G.objectsInRect(shownScene(), r).map((o) => o.id);
        select(S.expandToGroups(scene, d.additive ? [...new Set([...selected.objectIds, ...ids])] : ids));
        break;
      }
      case 'pan':
        break;
    }
  };

  const onWheel = (e: WheelEvent<HTMLDivElement>) => {
    if (!ready) return;
    const factor = Math.exp(-e.deltaY * 0.0015);
    userCamera(G.zoomAt(cameraRef.current, factor, toScreen(e)));
  };

  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    const sid = scene.id;
    const ids = selectedObjects.filter((o) => !S.isLocked(scene, o)).map((o) => o.id);
    const mod = e.ctrlKey || e.metaKey;
    const selectable = scene.objects.filter((o) => S.isShown(scene, o) && !S.isLocked(scene, o));
    const step = e.shiftKey ? 10 : 1;
    const nudge = (dx: number, dy: number) => ids.length && onTransform('Nudge', ids, (o) => ({ x: o.x + dx, y: o.y + dy }), `nudge:${ids.join(',')}`);
    let handled = true;
    if (mod && e.key.toLowerCase() === 'z') {
      const message = e.shiftKey ? history.redo() : history.undo();
      if (message) setAnnounce(message);
    } else if (mod && e.key.toLowerCase() === 'y') {
      const message = history.redo();
      if (message) setAnnounce(message);
    } else if (mod && e.key.toLowerCase() === 'a') {
      select(selectable.map((o) => o.id));
    } else if (mod && e.key.toLowerCase() === 'd') {
      if (ids.length) {
        let created: string[] = [];
        if (
          onEdit('Duplicate', (p) => {
            const r = S.duplicateObjects(p, sid, ids);
            created = r.ids;
            return r.project;
          })
        )
          select(created);
      }
    } else if (e.key === 'ArrowLeft') nudge(-step, 0);
    else if (e.key === 'ArrowRight') nudge(step, 0);
    else if (e.key === 'ArrowUp') nudge(0, -step);
    else if (e.key === 'ArrowDown') nudge(0, step);
    else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (ids.length && onEdit('Delete', (p) => S.deleteObjects(p, sid, ids))) select([]);
    } else if (e.key === 'Escape') {
      if (drag.current) {
        controllerRef.current?.setPreview(null);
        drag.current = null;
        setMarquee(null);
      } else select([]);
    } else if (e.key === 'Tab' && !mod && selectable.length) {
      // Step through selectable objects (top-most first), like a focus ring. Past the last
      // (or before the first, with Shift) the key is left to the browser, so keyboard focus
      // can always leave the canvas (no keyboard trap).
      const ordered = [...selectable].reverse();
      const current = ordered.findIndex((o) => o.id === selected.objectIds[0]);
      const index = current === -1 ? (e.shiftKey ? -1 : 0) : current + (e.shiftKey ? -1 : 1);
      if (index >= 0 && index < ordered.length) select([ordered[index]!.id]);
      else {
        select([]);
        handled = false;
      }
    } else if (e.key === ']' || e.key === '}') {
      // Shift+] arrives as "}" on most layouts.
      const front = e.shiftKey || e.key === '}';
      if (ids.length) onEdit(front ? 'Bring to front' : 'Bring forward', (p) => S.reorderObjects(p, sid, ids, front ? 'front' : 'forward'));
    } else if (e.key === '[' || e.key === '{') {
      const back = e.shiftKey || e.key === '{';
      if (ids.length) onEdit(back ? 'Send to back' : 'Send backward', (p) => S.reorderObjects(p, sid, ids, back ? 'back' : 'backward'));
    }
    else if (e.key === '+' || e.key === '=') userCamera(G.zoomAt(cameraRef.current, 1.25, centre()));
    else if (e.key === '-') userCamera(G.zoomAt(cameraRef.current, 0.8, centre()));
    else if (e.key === '0') fit();
    else handled = false;
    if (handled) e.preventDefault();
  };
  const centre = () => {
    const view = controllerRef.current?.viewSize ?? { width: 0, height: 0 };
    return { x: view.width / 2, y: view.height / 2 };
  };

  const summary = selectedObjects.length === 0 ? 'nothing selected' : selectedObjects.length === 1 ? `${selectedObjects[0]!.name} selected` : `${selectedObjects.length} objects selected`;
  return (
    <div className="relative">
      <div
        ref={stageRef}
        className="relative h-[52vh] min-h-72 w-full overflow-hidden rounded-lg border border-zinc-800 bg-zinc-950 sm:h-[60vh]"
        data-testid="animation-stage"
        data-zoom={camera.zoom.toFixed(4)}
        data-pan={`${camera.panX.toFixed(1)},${camera.panY.toFixed(1)}`}
      >
        {/* Input layer above the Pixi canvas: a custom widget with full keyboard support
            (see the aria-label), so it needs pointer/keyboard handlers and focus. */}
        {/* eslint-disable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        <div
          role="application"
          aria-roledescription="scene canvas"
          aria-label={`Scene “${scene.name}”, ${scene.width} × ${scene.height}, ${scene.objects.length} objects, ${summary}. Arrow keys move, Tab selects the next object (and leaves the canvas after the last one), Delete removes, Escape clears the selection, Ctrl+Z undoes.`}
          tabIndex={0}
          className="absolute inset-0 z-10 cursor-default touch-none outline-none focus-visible:ring-2 focus-visible:ring-accent-400"
          data-testid="scene-input"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={(e) => finish(e)}
          onPointerCancel={(e) => finish(e, true)}
          onWheel={onWheel}
          onKeyDown={onKeyDown}
          onContextMenu={(e) => e.preventDefault()}
        />
        {/* eslint-enable jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/no-noninteractive-tabindex */}
        {marquee && (
          <div
            aria-hidden="true"
            className="pointer-events-none absolute z-20 border border-sky-400 bg-sky-400/10"
            style={{ left: marquee.x, top: marquee.y, width: marquee.w, height: marquee.h }}
          />
        )}
        <div className="absolute right-2 bottom-2 z-20 flex items-center gap-1 rounded-md border border-zinc-700 bg-zinc-900/90 p-1" role="group" aria-label="Zoom">
          <button type="button" className="size-8 rounded text-sm hover:bg-zinc-800" aria-label="Zoom out" onClick={() => userCamera(G.zoomAt(cameraRef.current, 0.8, centre()))}>
            −
          </button>
          <span className="w-12 text-center font-mono text-xs text-zinc-300" data-testid="zoom-level">
            {Math.round(camera.zoom * 100)}%
          </span>
          <button type="button" className="size-8 rounded text-sm hover:bg-zinc-800" aria-label="Zoom in" onClick={() => userCamera(G.zoomAt(cameraRef.current, 1.25, centre()))}>
            +
          </button>
          <button type="button" className="h-8 rounded px-2 text-xs hover:bg-zinc-800" onClick={fit} data-testid="zoom-fit">
            Fit
          </button>
        </div>
        {status === 'loading' && (
          <div className="absolute inset-0 grid place-items-center">
            <Spinner label="Starting renderer…" />
          </div>
        )}
      </div>
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
    </div>
  );
}
